import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Volume2,
  LayoutTemplate,
  SquareStack,
  Flame,
  Paintbrush,
  Hammer,
  Brush,
  Layers,
  DoorOpen,
  ArrowUpRight,
} from "lucide-react";

export const ALL_SERVICES = [
  {
    key: "commercial-fit-out",
    icon: Building2,
    title: "Commercial Fit-Out",
    short:
      "Cat-A and Cat-B fit-outs for offices, retail and hospitality. Delivered on programme with one point of contact.",
    large: true,
  },
  {
    key: "acoustic-solutions",
    icon: Volume2,
    title: "Acoustic Solutions",
    short:
      "Acoustic panels, ceilings and sound-control installations for offices, meeting rooms and commercial interiors.",
    tag: "Specialist",
    large: true,
  },
  {
    key: "partitions-drylining",
    icon: LayoutTemplate,
    title: "Partitions & Drylining",
    short:
      "Metal-stud, timber and acoustic partitions. Fire-rated and high-performance systems to spec.",
  },
  {
    key: "suspended-ceilings",
    icon: SquareStack,
    title: "Suspended Ceilings",
    short:
      "MF, suspended grid and plasterboard ceilings — including acoustic and fire-rated solutions.",
  },
  {
    key: "fire-door-installation",
    icon: Flame,
    title: "Fire Door Installation",
    short:
      "Certified FD30 and FD60 fire door installation, inspection and remedial works.",
  },
  {
    key: "painting-finishing",
    icon: Paintbrush,
    title: "Painting & Finishing",
    short:
      "High-spec preparation, spray and hand-finished decoration to commercial-grade standards.",
  },
  {
    key: "demolition-strip-out",
    icon: Hammer,
    title: "Demolition & Strip-Out",
    short: "Soft strip, waste clearance and site preparation. The first stage of every project.",
    href: "/demolition-strip-out-london",
    tag: "Stage 01",
  },
  {
    key: "plastering",
    icon: Brush,
    title: "Plastering",
    short: "Skim, float-and-set, re-plastering and patch repairs — mirror-flat walls.",
  },
  {
    key: "flooring-tiling",
    icon: Layers,
    title: "Flooring & Tiling",
    short: "Engineered wood, LVT, vinyl, porcelain, ceramic and natural stone.",
  },
  {
    key: "carpentry-doors",
    icon: DoorOpen,
    title: "Carpentry & Doors",
    short: "1st and 2nd fix carpentry, bespoke joinery and door installation.",
  },
];

function ServiceCard({ s, i }) {
  const Icon = s.icon;
  const Inner = (
    <motion.div
      data-testid={`services-overview-card-${s.key}`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: i * 0.04 }}
      className={`group relative border-r border-b border-[#E5DDD0] bg-white hover:bg-[#F5EFE5] transition-colors p-7 md:p-8 min-h-[240px] flex flex-col justify-between ${
        s.large ? "md:col-span-2" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <Icon
          className="w-7 h-7 text-[#B8732C] group-hover:text-[#C9A24C] transition-colors"
          strokeWidth={1.5}
        />
        <ArrowUpRight className="w-5 h-5 text-[#8A7F74] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
      <div className="mt-8">
        {s.tag && (
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C] font-bold mb-2">
            {s.tag}
          </div>
        )}
        <h3
          className="font-black tracking-tight text-xl md:text-2xl leading-[1.1] text-[#2B2420]"
          style={{ fontFamily: "Cabinet Grotesk" }}
        >
          {s.title}
        </h3>
        <p className="mt-3 text-sm text-[#8A7F74] leading-relaxed">{s.short}</p>
      </div>
    </motion.div>
  );
  if (s.href) {
    return (
      <Link to={s.href} className={s.large ? "md:col-span-2" : ""}>
        {Inner}
      </Link>
    );
  }
  return (
    <a href="#quote" className={s.large ? "md:col-span-2" : ""}>
      {Inner}
    </a>
  );
}

export default function ServicesOverview({
  eyebrow = "Our Services",
  title = "Commercial contractors. End-to-end delivery.",
  subtitle = "From fit-outs and acoustic installations to full refurbishments — every discipline delivered in-house across London.",
  showViewAll = true,
}) {
  return (
    <section
      id="services"
      data-testid="services-overview-section"
      className="bg-white text-[#2B2420] py-20 md:py-28"
    >
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5 font-bold">
              <span className="brand-gold-divider" /> {eyebrow}
            </div>
            <h2
              className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl max-w-3xl"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              {title}
            </h2>
          </div>
          <p className="max-w-md text-[#8A7F74] text-base leading-relaxed">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-[#E5DDD0]">
          {ALL_SERVICES.map((s, i) => (
            <ServiceCard s={s} i={i} key={s.key} />
          ))}
        </div>

        {showViewAll && (
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link
              to="/services"
              data-testid="services-overview-view-all"
              className="inline-flex items-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
            >
              See all services <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/demolition-strip-out-london"
              data-testid="services-overview-demolition-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B2420] hover:text-[#B8732C] underline-offset-4 underline"
            >
              Or go straight to Demolition &amp; Strip-Out →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
